import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import type { CSSProperties, RefObject } from "react";

/**
 * W-2: panel plywajacy (lista selecta, tooltip) NIGDY nie wychodzi poza ekran -
 * odbija sie od krawedzi.
 *
 * Jeden hook dla obu uzyc. Powod jest konkretny: gdyby Select i Tooltip liczyly
 * pozycje osobno, W-2 byloby rozwiazane dwa razy i dwa razy inaczej - a przy trzecim
 * panelu pojawilby sie trzeci wariant. Roznice miedzy uzyciami sprowadzaja sie do
 * czterech parametrow ponizej.
 *
 * Panel jest pozycjonowany `position: fixed`, czyli wzgledem viewportu, a nie
 * przewijanego rodzica. To swiadome: kotwica moze siedziec w kontenerze z
 * `overflow: hidden` (karta, .rowlist), gdzie panel absolutny zostalby przyciety.
 */

export type StronaPanelu = "nad" | "pod";

interface Opcje {
  /** Panel jest widoczny - tylko wtedy liczymy i nasluchujemy. */
  otwarty: boolean;
  kotwicaRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  /** Gdzie panel ma stanac, jesli jest miejsce. Select: "pod", tooltip: "nad" (TT-3). */
  preferowanaStrona: StronaPanelu;
  /** "start" trzyma lewa krawedz kotwicy, "srodek" centruje panel na kotwicy. */
  wyrownanie: "start" | "srodek";
  /** Panel dostaje szerokosc kotwicy (select wyglada wtedy jak przedluzenie pola). */
  dopasujSzerokosc?: boolean;
  /** Odstep miedzy kotwica a panelem w px. */
  odstep?: number;
  /**
   * Cokolwiek, czego zmiana ma wymusic przeliczenie przy wciaz otwartym panelu.
   * Tooltip ma JEDNA instancje na dokument (TT-3), wiec przejscie kursora z jednego
   * wyzwalacza na drugi nie zamyka panelu - zmienia sie tylko kotwica, a `ref.current`
   * nie jest reaktywny i sam z siebie nie odpalilby efektu.
   */
  klucz?: unknown;
}

interface Wynik {
  /** Do wpiecia w `style` panelu. Dopoki nie policzono - panel stoi poza ekranem. */
  styl: CSSProperties;
  /** Po ktorej stronie kotwicy panel realnie wyladowal (przydatne dla strzalki). */
  strona: StronaPanelu;
}

/** Margines bezpieczenstwa od krawedzi okna. Wartosc z gotowca tooltip.html. */
const MARGINES = 12;

export function useAnchoredPanel({
  otwarty,
  kotwicaRef,
  panelRef,
  preferowanaStrona,
  wyrownanie,
  dopasujSzerokosc = false,
  odstep = 8,
  klucz,
}: Opcje): Wynik {
  const [styl, setStyl] = useState<CSSProperties>({});
  const [strona, setStrona] = useState<StronaPanelu>(preferowanaStrona);

  const przelicz = useCallback(() => {
    const kotwica = kotwicaRef.current;
    const panel = panelRef.current;
    if (!kotwica || !panel) return;

    const prostokat = kotwica.getBoundingClientRect();
    const szerokoscPanelu = dopasujSzerokosc ? prostokat.width : panel.offsetWidth;
    const wysokoscPanelu = panel.offsetHeight;
    const oknoW = window.innerWidth;
    const oknoH = window.innerHeight;

    let lewo = wyrownanie === "srodek" ? prostokat.left + prostokat.width / 2 - szerokoscPanelu / 2 : prostokat.left;
    // Odbicie od krawedzi bocznych. Math.max na koncu chroni przed panelem szerszym
    // niz okno - wtedy trzymamy go przy lewej krawedzi zamiast wypychac w minus.
    lewo = Math.max(MARGINES, Math.min(lewo, oknoW - szerokoscPanelu - MARGINES));

    const podKotwica = prostokat.bottom + odstep;
    const nadKotwica = prostokat.top - wysokoscPanelu - odstep;

    let wybrana: StronaPanelu = preferowanaStrona;
    if (preferowanaStrona === "pod") {
      const nieMiesciSiePod = podKotwica + wysokoscPanelu > oknoH - MARGINES;
      if (nieMiesciSiePod && nadKotwica >= MARGINES) wybrana = "nad";
    } else {
      if (nadKotwica < MARGINES) wybrana = "pod";
    }

    setStrona(wybrana);
    setStyl({
      position: "fixed",
      top: wybrana === "pod" ? podKotwica : Math.max(MARGINES, nadKotwica),
      left: lewo,
      ...(dopasujSzerokosc ? { width: szerokoscPanelu } : {}),
    });
    // `klucz` jest w zaleznosciach celowo, mimo ze cialo go nie czyta: wymusza swiezy
    // przelicz przy podmianie kotwicy bez zamykania panelu.
  }, [kotwicaRef, panelRef, preferowanaStrona, wyrownanie, dopasujSzerokosc, odstep, klucz]);

  // useLayoutEffect, nie useEffect: pozycje liczymy PRZED malowaniem, inaczej panel
  // mignalby w lewym gornym rogu zanim trafi na miejsce.
  useLayoutEffect(() => {
    if (!otwarty) return;
    przelicz();
  }, [otwarty, przelicz]);

  useEffect(() => {
    if (!otwarty) return;
    // `true` w trzecim argumencie to faza przechwytywania - inaczej nie zlapiemy
    // przewijania kontenera wewnetrznego, tylko okna.
    window.addEventListener("scroll", przelicz, true);
    window.addEventListener("resize", przelicz);
    return () => {
      window.removeEventListener("scroll", przelicz, true);
      window.removeEventListener("resize", przelicz);
    };
  }, [otwarty, przelicz]);

  return { styl, strona };
}
